import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, map, mergeMap, of, switchMap } from 'rxjs';
import { AuthActions } from '../../components/auth/auth.actions';
import { ReportActions } from './report.actions';
import { ReportService } from './report.service';

/**
 * @description Report Effects.
 * @exports
 * @class ReportEffects
 */
@Injectable()
export class ReportEffects extends ReportService {
  private readonly actions = inject(Actions);

  readonly onAuthChange = createEffect(
    () => {
      return this.actions.pipe(
        ofType(
          AuthActions.logout,
          AuthActions.login,
          AuthActions.switchProfile
        ),
        switchMap(() => of(ReportActions.fetchReports(), ReportActions.reset()))
      );
    },
    { dispatch: true }
  );

  readonly onFetchDNAReportFigure = createEffect(
    () => {
      return this.actions.pipe(
        ofType(ReportActions.fetchDNAReportFigure),
        mergeMap(({ reportType }) =>
          this.findDNAReportFigure({
            where: {
              reportType: reportType as any
            }
          }).pipe(
            map((data) =>
              ReportActions.fetchDNAReportFigureSuccess({
                data: data,
                reportType
              })
            ),
            catchError(() =>
              of(ReportActions.fetchDNAReportFigureFailed({ reportType }))
            )
          )
        )
      );
    },
    {
      dispatch: true
    }
  );

  readonly onFetchLabTrackings = createEffect(() => {
    return this.actions.pipe(
      ofType(ReportActions.fetchLabTrackingFromReports),
      switchMap(() => {
        return this.findAllLabTracking({});
      }),
      map((response) => {
        return ReportActions.fetchLabTrackingFromReportsSuccess({
          count: response?.count ?? 0,
          items: response?.results ?? []
        });
      }),
      catchError((error) =>
        of(
          ReportActions.fetchLabTrackingFromReportsFailed({
            error: {
              message: error?.message as string
            }
          })
        )
      )
    );
  });

  readonly onFetchReports = createEffect(
    () => {
      return this.actions.pipe(
        ofType(
          ReportActions.fetchReports,
          AuthActions.login,
          AuthActions.logout,
          AuthActions.switchProfile
        ),
        concatMap(() => {
          return this.findAllReportTypes({
            include: ['kit-related', 'unlock-purchase-status'],
            withCount: false,
            where: { isActive: true, isDNA: true }
          }).pipe(
            map(({ results }) =>
              ReportActions.fetchReportsSuccess({ reports: results })
            ),
            catchError((error) => {
              return of(
                ReportActions.fetchReportsFailed({
                  error: error?.message ?? 'Unknown error'
                })
              );
            })
          );
        })
      );
    },
    { dispatch: true }
  );
}
